<template>
	<EwPopover v-model="active" class="inplace-display relative inline-flex items-center" :class="{' hover:bg-primary-50/20' : collectionMode?.editmode }"
		:title="props.blockTitle" :showClose="true" placement="bottom" strategy="fixed">
		<div>
			<slot name="default" />
		</div>
		<button v-if=" collectionMode?.editmode " class="ml-2" v-tooltip.bottom="'Edit'" @click="active = true">
			<i class="tabler:edit w-[20px] h-[20px] flex m-0 text-dark no-print"></i>
		</button>
		<template #header>
			<div class="ew-popover-header mb-3">
				{{ props.blockTitle }}

				<div class="ml-auto flex gap-1 items-center">
					<EwTooltip>
					<template #content>
						<div class="text-center">
						Undo
						<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Z</kbd></div>
						</div>
					</template>

						<button type="button" class="undo-btn" :disabled="!canUndo" title="Undo" @click="undo()">
							<i class="i-uis:history flex text-[18px] font-500 !color-[#64748b]"></i>
						</button>
						
					</EwTooltip>
					<EwTooltip>
						<template #content>
							<div class="text-center">
								Redo
							<div class="color-gray"><kbd>Ctrl</kbd><kbd>+</kbd><kbd>Y</kbd></div>
							</div>
						</template>
						<button type="button" class="redo-btn" :disabled="!canRedo" title="Redo" @click="redo()">
							<i class="i-uis:history flex text-[18px] scale-x-[-1] font-500 !color-[#64748b]"></i>
						</button>
					</EwTooltip>
					<EwTooltip>
						<template #content>
							<div class="text-center">
								Close
							<div class="color-gray"><kbd>Esc</kbd></div>
							</div>
						</template>

						<i class="tabler:x w-[18px] h-[18px] flex ml-[3px] cursor-pointer " :ripple="true" @click="active = false"></i>
					</EwTooltip>
				</div>

			</div>
		</template>
		<template #content>
			<div v-if=" active " class="inplace-input inline-flex relative"
				:class="[{ 'flex-col gap-2': verticalOrientation }, { 'items-center gap-2': !verticalOrientation }]">
				<slot name="input">
					<EwLoadingOverlay v-if="isLoadingAssistant" :assistantLoadingMsg="'Asking assistant...'" class="!h-auto" />

					<template v-if=" editContentList.length ">
						<InplaceInput @update:content="onTextChange"
							v-for=" ( inputProp, propInd ) in props.inputProps " :key="inputProp.propName"
							v-bind="inputProp" v-model:content="editContentList[propInd].content" />
							
					</template>
					<template v-else-if=" !!editContentSingle ">
						<InplaceInput v-bind="editContentSingle" v-model:content="editContentSingle.content" />
					</template>
				</slot>
				<div class="flex items-center gap-2 mt-3">
					<EwButton type="primary" :size="'sm'" :loading="savingContent" @click="applyChange()">
						<i class="tabler:check flex m-0"></i>
						Save 
					</EwButton>
					<EwButton type="secondary" :size="'sm'" @click="discardChanges">
						<i class="tabler:x flex m-0"></i>
						Discard 
					</EwButton>
					<askAssistant
						v-if="showAssistant"
						@ask="handleAssistantRequest"
						@update:content="onTextChange"
						v-bind="editContentSingle"
						:loading="isLoadingAssistant"
						@keydown.enter.stop.prevent="handleAssistantRequest"
					/>
					<EwButton
						type="secondary"
						:size="'sm'"
						:tooltip="'Base language text'"
						v-if="enableCollapseSection && collectionMode?.editmode && (collectionMode?.id=='authoring' || collectionMode?.id=='language_review') && 
						props.extraContent "
						@click="showExtra = !showExtra"
					>
						<i :class="['tabler:chevron-down transition-transform', { 'rotate-180': showExtra }]"></i>
					</EwButton>

				</div>
			</div>
			<transition name="fade">
				<div v-if="enableCollapseSection && showExtra && props.extraContent" class="mt-4 p-3 border rounded bg-gray-50 w-full space-y-3 text-gray-700 text-sm">
						<!-- Nëse është string -->
						<div v-if="typeof props.extraContent === 'string'" v-html="props.extraContent"></div>

						<!-- Nëse është array -->
						<div v-else-if="Array.isArray(props.extraContent)">
							<div v-for="(item, idx) in props.extraContent" :key="idx" class="mb-2">
								<div v-html="item"></div>
							</div>
						</div>
				</div>
			</transition>

		</template>
	</EwPopover>
</template>

<script setup lang="ts">
import { useEventBus, useManualRefHistory, useDebounceFn, useMagicKeys } from '@vueuse/core';
import { useAppConfig } from '@/composables/useAppConfig';
import InplaceInput from './InplaceInput.vue';
import type { InplaceInputProps, InplaceInputPropSingle, InputPropType } from './';
import askAssistant from './askAssistant.vue';
import { EwTooltip } from '@arkit/ew-vue-components';
import EwLoadingOverlay from '@/components/EwLoadingOverlay.vue';
import { useRoute } from 'vue-router'
import type { configCollectionMode } from '@/types';

const showExtra = ref(false)


function deepClone<T>(val: T): T {
	return JSON.parse(JSON.stringify(val))
}

const props = withDefaults(
  defineProps<{
    href?: string;
    inputProps?: Array<InplaceInputProps & { propName: string }>;
    inputProp?: InplaceInputPropSingle | null;
    blockTitle?: string;
    method?: 'PATCH' | 'PUT' | 'POST';
    collectionMode?: configCollectionMode | null;
	enableCollapseSection?: boolean;
	extraContent?: string | string[] | Record<string, string> | null

  }>(),
  {
    inputProps: () => [],
    inputProp: null,
    blockTitle: 'Edit property',
    method: 'PATCH',
    collectionMode: null,
	enableCollapseSection: false,
	extraContent: null
  }
)

const emit = defineEmits(['saved']);
const bus = useEventBus<Record<string, any>>('saved');
const route = useRoute();
const AppConfig = useAppConfig();

const active = defineModel<boolean>('active', { default: false });

const editContentList = ref<InplaceInputProps[]>(props.inputProps.length ? deepClone(props.inputProps) : []);
const editContentSingle = ref<InplaceInputPropSingle | null>(props.inputProp ? deepClone(props.inputProp) : null);



if (
	editContentSingle.value &&
	(editContentSingle.value.content === undefined || editContentSingle.value.content === null)
) {
	// @ts-ignore
	editContentSingle.value.content = {}
}

const {
	history: listHistory,
	commit: commitList,
	undo: undoList,
	redo: redoList,
	clear: clearList,
	canUndo: canUndoList,
	canRedo: canRedoList,
} = useManualRefHistory(editContentList, { clone: true });

const {
	history: singleHistory,
	commit: commitSingle,
	undo: undoSingle,
	redo: redoSingle,
	clear: clearSingle,
	canUndo: canUndoSingle,
	canRedo: canRedoSingle,
} = useManualRefHistory(editContentSingle, { clone: true });

const isSingleMode = computed(() => !props.inputProps.length && !!props.inputProp);
const canUndo = computed(() => isSingleMode.value ? canUndoSingle.value : canUndoList.value);
const canRedo = computed(() => isSingleMode.value ? canRedoSingle.value : canRedoList.value);

function undo() {
	isSingleMode.value ? undoSingle() : undoList()
}

function redo() {
	isSingleMode.value ? redoSingle() : redoList()
}

function clear() {
	isSingleMode.value ? clearSingle() : clearList()
}

function commit() {
	isSingleMode.value ? commitSingle() : commitList()
}

function discardChanges() {
	editContentList.value = deepClone(props.inputProps)
	editContentSingle.value = props.inputProp ? deepClone(props.inputProp) : null
	clear()
	commit()
	active.value = false
}

watch(active, (newVal) => {
	if (newVal) {
		editContentList.value = deepClone(props.inputProps)
		editContentSingle.value = props.inputProp ? deepClone(props.inputProp) : null
		clear()
		commit()
	} else {
		errorSaving.value = false
	}
})

watch(
	() => ({
		href: props.href,
		inputProps: props.inputProps,
		inputProp: props.inputProp,
	}),
	() => {
		editContentList.value = deepClone(props.inputProps)
		editContentSingle.value = props.inputProp ? deepClone(props.inputProp) : null
		active.value = false
		clear()
		commit()
	},
	{ deep: true }
)

watch(() => route.query.lng, () => {
	editContentList.value = deepClone(props.inputProps)
	editContentSingle.value = props.inputProp ? deepClone(props.inputProp) : null
	active.value = false
	clear()
	commit()
})

// pjesa e mbetur e funksioneve: generateContentObject, applyChange, handleAssistantRequest etj...

const savingContent = ref(false);
const errorSaving = ref(false);
const isLoadingAssistant = ref(false)

const verticalOrientation = computed(() => {
	if (!props.inputProps.length && !!props.inputProp && shouldBeVertical(props.inputProp.inputType)) {
		return true
	}
	return props.inputProps.some((prop) => shouldBeVertical(prop.inputType));
});

const showAssistant = computed(() => {
	if (!props.inputProps.length && !!props.inputProp) {
		const type = props.inputProp.inputType
		return !['datepicker', 'select', 'packageTitle', 'references'].includes(type ?? '')
	}
	return props.inputProps.some(
		(prop) => !['datepicker', 'select', 'packageTitle', 'references'].includes(prop.inputType ?? '')
	)
})

function shouldBeVertical(inputType?: InputPropType) {
	return ['input', 'packageTitle', 'textarea', 'dropdown', 'select', 'datepicker', 'editor', 'references', 'quiz', 'assistant'].includes(inputType ?? '')
}

function getValueByPath(obj: any, path: string): any {
	return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

function sanitizePramas(data: Record<string, any> | string) {
	if (typeof data === 'string') return data
	return JSON.parse(JSON.stringify(data, (key, value) => (key === '_links' ? undefined : value)))
}

function getCleanedObject(content: Record<string, any>, propsToRemove: string[]) {
	return Object.keys(content).reduce((acc, key) => {
		if (!propsToRemove.includes(key)) acc[key] = content[key]
		return acc
	}, {} as Record<string, any>)
}

function generateContentObject() {
	const content: Record<string, any> = !editContentList.value.length && !!editContentSingle.value
		? { ...editContentSingle.value.content }
		: {};

	if (editContentList.value.length) {
		props.inputProps.forEach((prop, ind) => {
			const propKeys = prop.propName.split('.');
			propKeys.reduce((acc, curr, i) => {
				if (i === propKeys.length - 1) {
					acc[curr] = editContentList.value[ind].content;
				} else {
					if (!acc[curr]) acc[curr] = {};
				}
				return acc[curr];
			}, content);
		});
	}

	if (content._links) delete content._links;
	if (content.id) delete content.id;

	return content;
}

async function handleAssistantRequest(payload: { askAssistant: { userGuide: string } }) {
	const content = generateContentObject();
	content.askAssistant = payload.askAssistant;
	isLoadingAssistant.value = true;

	try {
		const response = await fetch(props.href, {
			method: props.method,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(content)
		});
		const responseData = await response.json();

		if (editContentSingle.value && responseData?.question) {
			editContentSingle.value.content = {
				...editContentSingle.value.content,
				question: responseData.question,
				answers: responseData.answers
			};
		} else {
			editContentList.value.forEach((item) => {
				const value = getValueByPath(responseData, item.propName);
				if (value !== undefined) item.content = value;
			});
		}
		commit();
	} catch (err) {
		console.error('[Ask Assistant Error]:', err)
	} finally {
		isLoadingAssistant.value = false
	}
}

async function applyChange() {
	let content: Record<string, any> = !editContentList.value.length && !!editContentSingle.value
		? { ...editContentSingle.value.content }
		: {};

	if (editContentList.value.length) {
		props.inputProps.forEach((prop, ind) => {
			const propKeys = prop.propName.split('.');
			propKeys.reduce((acc, curr, i) => {
				if (i === propKeys.length - 1) {
					acc[curr] = editContentList.value[ind].content;
				} else {
					if (!acc[curr]) acc[curr] = {};
				}
				return acc[curr];
			}, content);
		});
	}

	if (editContentSingle.value?.propName) {
		const propKeys = editContentSingle.value.propName.split('.');
		const newContent: Record<string, any> = {};
		propKeys.reduce((acc, curr, i) => {
			if (i === propKeys.length - 1) {
				acc[curr] = getCleanedObject({ ...editContentSingle.value!.content }, editContentSingle.value.removeProps ?? []);
			} else {
				if (!acc[curr]) acc[curr] = {};
			}
			return acc[curr];
		}, newContent);
		content = newContent;
	}

	if (content._links) delete content._links;
	if (content.id) delete content.id;

	savingContent.value = true;
	try {
		await fetch(props.href, {
			method: props.method,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(content)
		});
		errorSaving.value = false;
		emit('saved');
		bus.emit(content);
		setTimeout(() => (active.value = false), 300);
	} catch (error) {
		console.error(error);
		errorSaving.value = true;
	} finally {
		savingContent.value = false;
	}
}

const keys = useMagicKeys();
watch(keys['Ctrl+Z'], (v) => { if (v && canUndo.value) undo() })
watch(keys['Ctrl+Y'], (v) => { if (v && canRedo.value) redo() })

const onTextChange = useDebounceFn(() => commit(), 500);
</script>

<style>
.ew-popover {
	max-width: 800px;
	min-height: 100px;
}

.ew-select-root {
	overflow: visible;
}
.undo-btn,
.redo-btn {
	border: none;
	background: transparent;
	font-size: 20px;
	cursor: pointer;
	margin-top:3px;
}

button[disabled] {
	opacity: 0.4;
	/* pointer-events: none; */
	cursor:no-drop;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
